import {ProgramPaths} from "@/enum/ProgramPaths";
import {TakipDurumu} from "@/enum/TakipDurumu";

/**
 * DEV-1511: Takip Karti <-> Ajanda arasindaki cift yonlu gecisin tek kaynagi.
 *
 * Onceki denemede (DEV-1511, 29.07.2026) ajanda butonu dogrudan
 * `/ofispro/ajanda` adresine gidiyordu: hangi takipten gelindigi bilgisi
 * kayboluyor, ajandadan takip kartina donus de bulunmuyordu. Test bu yuzden
 * "yapilan islemler ihtiyaci karsilamamakta" dedi.
 *
 * Burada gecis, takibin izini query string'de tasiyacak bicimde uretilir:
 * `/ofispro/ajanda?takip=<id>&dosya=<dosya esas no>`. Ajanda ekrani bu izi
 * okuyup Is Listesi'ni o takibe filtreler ve "Takip Kartina Don" butonunu
 * gosterir. Iz URL'de durdugu icin sayfa yenilense (F5) veya link paylasilsa
 * da baglam kaybolmaz; hicbir global state / store alanina ihtiyac duyulmaz.
 */

export const AJANDA_YOLU: string = ProgramPaths.ofispro + '/ajanda';

/** Ajandaya tasinan takip izi. */
export interface AjandaTakipBaglami {
    takipId: number;
    /** Bos olabilir: takip acilmadan once dosya esas no henuz yoktur. */
    dosyaEsasNo: string;
}

/** `$router.push` icin hedef; query yalnizca gercek bir takip izi varsa uretilir. */
export interface AjandaRotaHedefi {
    path: string;
    query?: { [key: string]: string };
}

/**
 * Router query degeri string ya da string[] olabilir (ayni parametre iki kez
 * verilirse vue-router dizi dondurur). Ilk anlamli degeri metne cevirir.
 */
function ilkDeger(value: any): string {
    if (Array.isArray(value)) {
        return value.length > 0 ? ilkDeger(value[0]) : '';
    }
    if (value === null || value === undefined) {
        return '';
    }
    return String(value).trim();
}

/**
 * Takip id'sini pozitif tam sayiya cevirir. Gecersiz/eksik degerde 0 doner ve
 * cagiran taraf baglamsiz davranir (buton gizlenir ya da duz ajandaya gidilir).
 */
function takipIdCoz(value: any): number {
    const metin = ilkDeger(value);
    if (!/^\d+$/.test(metin)) {
        return 0;
    }
    const id = Number(metin);
    return id > 0 ? id : 0;
}

/**
 * Takip Kartindan Ajandaya gecis hedefi.
 *
 * Takip id'si cozulemezse (ornegin karti henuz yuklenmemis bir ekrandan
 * cagrildiysa) baglamsiz ajanda adresi doner; buton yine de calisir.
 */
export function ajandaRotasi(takipId: any, dosyaEsasNo: any = null): AjandaRotaHedefi {
    const id = takipIdCoz(takipId);
    if (!id) {
        return {path: AJANDA_YOLU};
    }
    const query: { [key: string]: string } = {takip: String(id)};
    const dosya = ilkDeger(dosyaEsasNo);
    if (dosya) {
        query.dosya = dosya;
    }
    return {path: AJANDA_YOLU, query};
}

/**
 * Ajandadan (ya da Is Listesinden) Takip Kartina donus yolu.
 *
 * Acilmamis takipler `/takip/:id` altinda goruntulenemez; uygulamanin her
 * yerinde oldugu gibi (bkz. IsInfo.takibeGit) `takip-ekle` sihirbazina gidilir.
 */
export function takipKartiYolu(takipId: any, takipDurumu: any = null): string {
    const id = takipIdCoz(takipId);
    if (!id) {
        return '';
    }
    const segment = takipDurumu === TakipDurumu.acilmamis ? '/takip-ekle/' : '/takip/';
    return ProgramPaths.icrapro + segment + id;
}

/**
 * DEV-2788: Nottan ajandaya aktarilan etkinligin detayina, notun bagli oldugu
 * takibin kart numarasi (takip id'si) kullaniciya gorunmeyen bir HTML yorumu
 * olarak gomulur: `<!--kart:64-->`.
 *
 * - Neden detayda: etkinlik tablosunda takibe giden bir kolon yok, API'ye de
 *   dokunulmadi.
 * - Neden gorunmez: DEV-2945 "(Not Numarası: N)" izini kullaniciya anlamsiz
 *   oldugu icin kaldirdi; kart bilgisinin de gosterilmesi istenmedi. Ajanda
 *   popup'i detayi v-html ile bastigindan yorum ekrana cikmaz; EtkinlikForm
 *   duzenlerken izi ayirir, kaydederken geri ekler.
 * - Neden basta: `details` varchar(255) ve sunucu uzun metni sessizce
 *   kirpiyor; sonda duran iz uzun notlarda kaybolurdu.
 */
const KART_IZI = /<!--kart:(\d+)-->/;

/** Takip id'sinden gizli kart izi; takip yoksa bos metin. */
export function kartIziOlustur(takipId: any): string {
    const id = takipIdCoz(takipId);
    return id ? '<!--kart:' + id + '-->' : '';
}

/** Detayi, formda gosterilecek metin ve gizli kart izi olarak ayirir. */
export function kartIziniAyir(details: any): { iz: string; metin: string } {
    const metin = typeof details === 'string' ? details : '';
    const eslesme = KART_IZI.exec(metin);
    if (!eslesme) {
        return {iz: '', metin: metin};
    }
    return {iz: eslesme[0], metin: metin.replace(KART_IZI, '')};
}

/** Kaydedilecek detay: gizli iz (varsa) her zaman en basta ve tek. */
export function kartIziEkle(metin: any, iz: string): string {
    const gorunen = kartIziniAyir(metin).metin;
    return iz ? iz + gorunen : gorunen;
}

/** Ajandadaki bir etkinligin takibe giden izi. */
export interface EtkinlikKaynagi {
    /**
     * `takip`: gizli kart izi takibi dogrudan verir.
     * `not` / `is`: takip, aktarilan kaydin kendisinden okunur; API kaynak
     * adiyla ayni (`/api/v1/not/:id`, `/api/v1/is/:id`).
     */
    tur: 'takip' | 'not' | 'is';
    id: number;
}

/**
 * DEV-2788: Etkinligin hangi takibe ait oldugunu detay metninden cozer.
 *
 * Once gizli kart izine bakilir (bkz. KART_IZI). Izi olmayan etkinlikler icin
 * EtkinlikForm'un eskiden beri detaya yazdigi numaraya dusulur:
 *   not -> "<not metni> - (Not Numarası: <not id>)"   (DEV-2945 oncesi kayitlar)
 *   is  -> "<aciklama> - (İş Numarası: <is id>)\nDosya Esas No: ..."
 *
 * Birden fazla numara varsa SONUNCUSU esas alinir: form numarayi her zaman
 * aktarilan metnin ARKASINA ekliyor, oncesindeki bir numara ise metnin
 * kendisine aittir (ornegin bir notu anan not ya da nottan uretilmis isin
 * aciklamasi).
 */
export function etkinlikKaynagiCoz(details: any): EtkinlikKaynagi | null {
    if (typeof details !== 'string' || !details) {
        return null;
    }
    const kart = KART_IZI.exec(details);
    const kartTakipId = kart ? takipIdCoz(kart[1]) : 0;
    if (kartTakipId) {
        return {tur: 'takip', id: kartTakipId};
    }
    const iz = /\((Not|İş) Numarası:\s*(\d+)\)/g;
    let kaynak: EtkinlikKaynagi | null = null;
    let eslesme: RegExpExecArray | null;
    while ((eslesme = iz.exec(details)) !== null) {
        const id = Number(eslesme[2]);
        if (Number.isSafeInteger(id) && id > 0) {
            kaynak = {tur: eslesme[1] === 'Not' ? 'not' : 'is', id: id};
        }
    }
    return kaynak;
}

/**
 * Ajanda ekraninin URL'deki takip izini okumasi icin. Iz yoksa ya da bozuksa
 * null doner -> ajanda her zamanki gibi baglamsiz acilir.
 */
export function ajandaBaglamiCoz(query: any): AjandaTakipBaglami | null {
    if (!query) {
        return null;
    }
    const takipId = takipIdCoz(query.takip);
    if (!takipId) {
        return null;
    }
    return {takipId: takipId, dosyaEsasNo: ilkDeger(query.dosya)};
}
